import { Field } from 'app/interfaces/fields';
import { GlobalService } from './global.service';
import { applyValueToField } from './synchronize';

function evaluate(expr: string): number | boolean {
    return Function(`"use strict";return (${expr})`)();
}

const BRACKET_REFS = /(?<=\[)[^\][]*(?=])/g;

function normalizePercent(expr: string): string {
    return expr.replace(/(\d+(\.\d+)?)\s*%\s*(\d+(\.\d+)?)/g, (_, a, _aDec, b) => {
        return `(${a} * (${b}/100))`;
    });
}

function getInputVal(uuid: string, globalService: GlobalService): string {
    const fieldData: Field = globalService.fields_data[uuid];
    const drag = document.querySelector(`.drag-fields[uuid="${uuid}"]`) as HTMLElement | null;
    const inputs = drag?.querySelectorAll('input, textarea') as NodeListOf<HTMLInputElement | HTMLTextAreaElement>;
    inputs?.forEach(input => input.classList.add('calculation'));
    return getValue(fieldData?.value);
}

function getValue(value: string): string {
    if (Array.isArray(value)) {
        // Try numeric sum first
        const numericValues = value.map(v => Number(v)).filter(v => !isNaN(v));

        if (numericValues.length === value.length) {
            return numericValues.reduce((a, b) => a + b, 0).toString();
        } else {
            return '0';
        }
    }
    return value?.toString() || '0';
}

function addParentLink(fields: Record<string, Field>, childUuid: string, parentUuid: string | number) {
    const arr = (fields[childUuid].connected_parent_calculation_id as (string | number)[]) ?? [];
    if (!arr.includes((parentUuid))) arr.push(parentUuid);
    fields[childUuid].connected_parent_calculation_id = arr;
}

function replaceNamesWithUuidsForStorage(evalStr: string, fields: Record<string, Field>) {
    const names = evalStr.match(BRACKET_REFS) || [];
    const resolved: string[] = [];

    let out = evalStr;
    for (const name of names) {
        const uu = Object.keys(fields).find(k => fields[k].select_type_text === name);
        if (!uu) continue;
        out = out.replace(new RegExp(`\\[${name}\\]`, 'g'), `[${uu}]`);
        resolved.push(uu);
    }
    return { evalStr: out, uuids: [...new Set(resolved)] };
}

function substituteByUuid(expr: string, globalService: GlobalService): string {
    const refs = expr.match(BRACKET_REFS) || [];
    let out = expr;
    for (const ref of refs) {
        const val = getInputVal(ref, globalService);
        out = out.replace(new RegExp(`\\[${ref}\\]`, 'g'), val).trim();
    }
    return out;
}

function parseIf(raw: string) {
    const cleaned = raw
        .replace(/= ?if/i, '')
        .replace(/[()]/g, '')
        .replace(/['"]/g, '');
    const parts = cleaned.split(',').map(s => s.trim());
    if (parts.length < 3) return null;
    const [condition, truthy, falsy] = parts;
    return { condition, truthy, falsy };
}

export function generateCalculation(uuid: string | number, globalService: GlobalService, stopInfinity = false) {
    const current = globalService.fields_data[String(uuid)];
    const parents = [...new Set(current?.connected_parent_calculation_id || [])];

    for (const parentId of parents) {
        const parent: Field = globalService.fields_data[String(parentId)];
        if (!parent?.enable_calculation) continue;

        const decimal = Number(parent.calculation_decimal ?? 0);
        const expr = String(parent.const_eval_calculation || '');

        // IF(...)
        if (/= ?if/i.test(expr)) {
            const parsed = parseIf(expr);
            if (!parsed) continue;

            const condExpr = substituteByUuid(parsed.condition, globalService);
            try {
                const result = evaluate(condExpr) ? parsed.truthy : parsed.falsy;
                applyValueToField(String(parentId), result, globalService, true);
            } catch {
                applyValueToField(String(parentId), '0', globalService, true);
            }
        } else {
            // arithmetic
            const resolved = normalizePercent(substituteByUuid(expr, globalService));
            try {
                const val = Number(evaluate(resolved));
                applyValueToField(String(parentId), val.toFixed(decimal), globalService, true);
            } catch {
                applyValueToField(String(parentId), '0', globalService, true);
            }
        }

        if (!stopInfinity) generateCalculation(String(parentId), globalService, false);
    }
}

export function onSaveCalculation(uuid: string | number, formula: string, globalService: GlobalService) {
    if (!formula) return;

    const fields = globalService.fields_data;
    const decimal = Number(fields[uuid].calculation_decimal ?? 0);

    // Convert [name] -> [uuid] for storage & link parents
    const { evalStr, uuids } = replaceNamesWithUuidsForStorage(formula, fields);
    for (const childUuid of uuids) addParentLink(fields, childUuid, uuid);

    // Evaluate immediate preview using current values
    if (/= ?if/i.test(formula)) {
        const parsed = parseIf(formula);
        if (parsed) {
            // condition uses names right now → convert to uuids for substitution
            let condForEval = replaceNamesWithUuidsForStorage(parsed.condition, fields).evalStr;
            condForEval = substituteByUuid(condForEval, globalService);
            try {
                const result = evaluate(condForEval) ? parsed.truthy : parsed.falsy;
                applyValueToField(String(uuid), result, globalService, true);
            } catch {
                applyValueToField(String(uuid), '0', globalService, true);
            }
        }
    } else {
        // arithmetic uses names → convert to uuids → substitute → normalize %
        let expr = replaceNamesWithUuidsForStorage(formula, fields).evalStr;
        expr = normalizePercent(substituteByUuid(expr, globalService));
        try {
            const val = Number(evaluate(expr));
            applyValueToField(String(uuid), val.toFixed(decimal), globalService, true);
        } catch {
            applyValueToField(String(uuid), '0', globalService, true);
        }
    }

    globalService.updateField(uuid, {
        eval_calculation: evalStr,
        const_eval_calculation: evalStr,
        calculation: formula
    });
}

export function clearCalculations(fields: Record<string, { eval_calculation?: string; connected_parent_calculation_id: (string | number)[] }>, currentUuid: string | number, globalService: GlobalService) {
    const expr = fields[currentUuid]?.eval_calculation || '';
    const refs = (expr.match(BRACKET_REFS) || []);
    for (const ref of refs) {
        if (!fields[ref]) continue;
        fields[ref].connected_parent_calculation_id = (fields[ref].connected_parent_calculation_id || []).filter(id => id != currentUuid);
        applyValueToField(String(ref), '', globalService, true);
        const drag = document.querySelector(`.drag-fields[uuid="${ref}"]`) as HTMLElement | null;
        const inputs = drag?.querySelectorAll('input, textarea') as NodeListOf<HTMLInputElement | HTMLTextAreaElement>;
        inputs?.forEach(input => input.classList.remove('calculation'));
    }
    applyValueToField(String(currentUuid), '', globalService, true);
}
